import React, {
  CSSProperties,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import Slider from "react-slick";
import { format } from "date-fns";
import classnames from "classnames";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

import { Dialog, IconButton } from "fe-shared/src/components";
import { FullscreenIcon } from "fe-shared/src/components/Icons";
import AttributesInfo from "../AttributesInfo";
import DetailsInfoBlock from "../DetailsInfoBlock";
import { ArrowRightBig, ArrowLeftBig } from "../Icons";
import { isTransaction } from "../../helpers/transactions";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import { InfoRow, Transaction, Transfer } from "../../types";

import styles from "./DetailsModal.module.scss";

interface Props {
  transactionOrTransfer: Transaction | Transfer;
  selectedImage: string | null;
  isDialogActive: boolean;
  isWeightTicketsInfo?: boolean;
  onCloseDialog: () => void;
  weightInfo?: InfoRow[];
  materialsInfo: InfoRow[];
}

interface ArrowProps {
  onClick?: () => void;
  style?: CSSProperties;
  className?: string;
}

const NextArrow = (props: ArrowProps) => {
  const { onClick, className, style } = props;

  return (
    <div className={classnames(className, styles.sliderArrow)} style={style}>
      <IconButton
        id="slider-next-arrow"
        aria-label="slider-next-arrow"
        onClick={onClick}
      >
        <ArrowRightBig fillColor={null} />
      </IconButton>
    </div>
  );
};

const PrevArrow = (props: ArrowProps) => {
  const { onClick, className, style } = props;

  return (
    <div className={classnames(className, styles.sliderArrow)} style={style}>
      <IconButton
        id="slider-prev-arrow"
        aria-label="slider-prev-arrow"
        onClick={onClick}
      >
        <ArrowLeftBig fillColor={null} />
      </IconButton>
    </div>
  );
};

const DetailsModal = ({
  transactionOrTransfer,
  selectedImage,
  isDialogActive,
  onCloseDialog,
  weightInfo,
  materialsInfo,
  isWeightTicketsInfo = true,
}: Props) => {
  const { t } = useTranslation();
  const { materialPhotos, paperFormImages, comment } = transactionOrTransfer;
  const [mainSlider, setMainSlider] = useState<Slider | undefined>();
  const [navigationSlider, setNavigationSlider] = useState<
    Slider | undefined
  >();

  const scaleTickets = useMemo(
    () =>
      isTransaction(transactionOrTransfer)
        ? transactionOrTransfer.scaleTickets
        : [],
    [transactionOrTransfer]
  );

  const images = useMemo(() => {
    const scaleTicketsImages = scaleTickets
      .filter((item) => !!item.ticket)
      .map((item) => item.ticket);

    return [
      ...(paperFormImages || []),
      ...materialPhotos,
      ...scaleTicketsImages,
    ];
  }, [scaleTickets, materialPhotos, paperFormImages]);

  const [activeSlide, setActiveSlide] = useState(
    images.indexOf(selectedImage) || 0
  );

  const sliderImages = images.map((photo, index) => (
    <div key={photo} className={styles.imageStyle}>
      <img src={photo!} alt={`#${index + 1}`} />
    </div>
  ));

  const weightAndScaleInfo = useMemo(
    () => [
      ...(weightInfo || []),
      ...scaleTickets.reduce(
        (result, s, idx) => [
          ...result,
          {
            key: `ticket-number-${idx}`,
            label: t("transactions.details.scaleTicket.number"),
            value: s.ticketNumber,
          },
          {
            key: `ticket-date-${idx}`,
            label: t("transactions.details.scaleTicket.date"),
            value: format(new Date(s.createdDate), "MMM d, yyyy"),
          },
        ],
        [] as InfoRow[]
      ),
    ],
    [t, weightInfo, scaleTickets]
  );

  useEffect(() => {
    const initialSlideIndex =
      selectedImage && images.findIndex((item) => item === selectedImage);

    if (initialSlideIndex) {
      mainSlider?.slickGoTo(initialSlideIndex);
      navigationSlider?.slickGoTo(initialSlideIndex);
    }
  }, [mainSlider, navigationSlider, selectedImage, images]);

  const navSlidesToShow = Math.min(images.length, 4);

  const toNext = () => {
    mainSlider?.slickNext();
    navigationSlider?.slickNext();
  };

  const toPrev = () => {
    mainSlider?.slickPrev();
    navigationSlider?.slickPrev();
  };

  const isPrevArrow = activeSlide > 0;
  const isNextArrow = activeSlide < images.length - 1;

  const resetTimeout = useRef<NodeJS.Timeout>();

  useEffect(() => () => clearTimeout(resetTimeout.current!), []);

  return (
    <Dialog
      width={1000}
      title={null}
      isActive={isDialogActive}
      onClose={onCloseDialog}
      isHeaderHidden
    >
      <div className={styles.deliveredDialogWrapper}>
        <div className={styles.deliveredDialogContent}>
          <div className={styles.deliveredDialogMainSliderWrapper}>
            <TransformWrapper pan={{ velocity: false }}>
              {(props: any) => {
                const { resetTransform, scale } = props;
                const isInitialScale = scale === 1;

                return (
                  <>
                    <div className={styles.transformControls}>
                      {!isInitialScale && (
                        <IconButton
                          id="slider-reset-transform-btn"
                          aria-label="slider-reset-transform-btn"
                          onClick={resetTransform}
                        >
                          <FullscreenIcon fillColor={null} />
                        </IconButton>
                      )}
                    </div>
                    {isInitialScale && (
                      <>
                        {isPrevArrow && (
                          <PrevArrow
                            onClick={toPrev}
                            className={styles.prevArrow}
                          />
                        )}
                        {isNextArrow && (
                          <NextArrow
                            onClick={toNext}
                            className={styles.nextArrow}
                          />
                        )}
                      </>
                    )}
                    <div
                      className={
                        styles.deliveredDialogTransformComponentWrapper
                      }
                    >
                      <TransformComponent>
                        <div className={styles.deliveredDialogTransformWrapper}>
                          <Slider
                            slidesToShow={1}
                            slidesToScroll={1}
                            fade
                            infinite={false}
                            speed={200}
                            className="main-slider"
                            asNavFor={navigationSlider}
                            draggable={false}
                            arrows={false}
                            afterChange={(s) => {
                              resetTimeout.current = setTimeout(
                                () => resetTransform(),
                                0
                              );
                              setActiveSlide(s);
                            }}
                            ref={(currentRef) => {
                              setMainSlider(currentRef || undefined);
                            }}
                          >
                            {sliderImages}
                          </Slider>
                        </div>
                      </TransformComponent>
                    </div>
                    <Slider
                      slidesToShow={navSlidesToShow}
                      slidesToScroll={1}
                      speed={200}
                      dots={false}
                      focusOnSelect
                      infinite={false}
                      variableWidth
                      draggable={false}
                      className="navigation-slider"
                      arrows={false}
                      asNavFor={mainSlider}
                      ref={(currentRef) => {
                        setNavigationSlider(currentRef || undefined);
                      }}
                    >
                      {sliderImages}
                    </Slider>
                  </>
                );
              }}
            </TransformWrapper>
          </div>
        </div>
        <div className={styles.deliveredDialogDescription}>
          <DetailsInfoBlock rows={materialsInfo} />
          <AttributesInfo transactionOrTransfer={transactionOrTransfer} />
          <p>{comment}</p>
          {isWeightTicketsInfo && (
            <DetailsInfoBlock rows={weightAndScaleInfo} />
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default DetailsModal;
